/** MeetingCard — 会议清单里的单场会议。
 *
 * 卡片要一眼答出三个问题：这场会在本系统里有没有内容（导入态）、三项 AI 产出各自
 * 到哪一步了、这场会需不需要现在就准备。
 *
 * 只有清单信息的会议不展示任何纪要相关内容——转录、摘要、待办对它都还不成立，展示
 * 它们会让律师以为纪要拉过而内容为空。这类会议只给一个导入入口。
 */

import { Button, Card, Space, Tag, Tooltip, Typography } from "antd";
import {
  AudioOutlined,
  BellOutlined,
  ClockCircleOutlined,
  CloudDownloadOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import type { Meeting } from "../../types";
import type { MeetingAnalysisState } from "../../types/meeting";
import {
  MEETING_SOURCE_LABELS,
  MEETING_STATUS_LABELS,
  TRANSCRIPT_STATUS_LABELS,
} from "../../types/meeting";
import MeetingProcessingStageTag from "./MeetingProcessingStageTag";

const { Text } = Typography;

function formatTimeRange(startsAt: string | null, endsAt: string | null): string {
  if (!startsAt) return "时间未定";
  const start = new Date(startsAt);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (!endsAt) return fmt(start);
  const end = new Date(endsAt);
  return `${fmt(start)} — ${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
}

const STATUS_TAG_COLOR: Record<Meeting["status"], string> = {
  upcoming: "blue",
  in_progress: "processing",
  ended: "default",
  cancelled: "red",
};

const TRANSCRIPT_BADGE_COLOR: Record<Meeting["transcriptStatus"], string> = {
  none: "default",
  pending: "gold",
  ready: "green",
  failed: "red",
};

/** 三项产出在卡片上的短标签。只在有进展时出现，从未生成的不占位置。 */
const ANALYSIS_TAG_COLOR: Record<MeetingAnalysisState, string> = {
  none: "default",
  pending: "processing",
  ready: "green",
  failed: "red",
};

function AnalysisTag({
  label,
  state,
  errorMessage,
}: {
  label: string;
  state: MeetingAnalysisState;
  errorMessage: string | null;
}) {
  if (state === "none") return null;
  const text =
    state === "pending"
      ? `${label}生成中`
      : state === "ready"
        ? `${label}已就绪`
        : `${label}未完成`;
  const tag = (
    <Tag color={ANALYSIS_TAG_COLOR[state]} style={{ marginInlineEnd: 0 }}>
      {text}
    </Tag>
  );
  return state === "failed" && errorMessage ? (
    <Tooltip title={errorMessage}>{tag}</Tooltip>
  ) : (
    tag
  );
}

interface MeetingCardProps {
  meeting: Meeting;
  onClick?: (meeting: Meeting) => void;
  /** 打开导入入口。只对仅有清单信息的会议出现。 */
  onImport?: (meeting: Meeting) => void;
  /** 触发会前准备。会前准备提示出现时可用。 */
  onPrepare?: (meeting: Meeting) => void;
  /** 左栏两栏布局里当前是否为已选中的会议。 */
  selected?: boolean;
}

export default function MeetingCard({
  meeting,
  onClick,
  onImport,
  onPrepare,
  selected = false,
}: MeetingCardProps) {
  const participantCount = meeting.participantEmails?.length ?? 0;
  const imported = meeting.importState === "imported";
  // 会前准备提示：定时扫描已选中这场会，且清单尚未生成。
  const prepPrompted =
    !!meeting.prepPromptedAt &&
    (meeting.prepState === "none" || meeting.prepState === "failed");

  return (
    <Card
      hoverable
      size="small"
      onClick={() => onClick?.(meeting)}
      styles={{
        body: { padding: "var(--jerry-sp-4) var(--jerry-sp-5)" },
      }}
      style={{
        marginBottom: "var(--jerry-sp-3)",
        borderColor: selected ? "var(--jerry-accent-primary)" : undefined,
        background: selected ? "var(--jerry-primary-subtle, var(--jerry-gray-50))" : undefined,
      }}
      data-testid="meeting-card"
      data-selected={selected || undefined}
    >
      {/* 三行结构（2026-08-27 重排，设计讨论底稿 L2-L4）：
          行1 标题独占整行完整显示（超长自然换行，不截断），未导入时右上角挂导入入口；
          行2 元信息单行不折行，尾部超长省略；
          行3 状态标签统一收到底部左对齐，取代原先右侧两行标签堆叠。 */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--jerry-sp-2)",
          marginBottom: "var(--jerry-sp-1)",
        }}
      >
        <VideoCameraOutlined
          style={{ color: "var(--jerry-primary-500)", marginTop: 3, flexShrink: 0 }}
        />
        <Text
          strong
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: "var(--jerry-font-size-default)",
            wordBreak: "break-word",
          }}
        >
          {meeting.title}
        </Text>
        {!imported && onImport && (
          <Tooltip title="导入内容">
            <Button
              size="small"
              icon={<CloudDownloadOutlined />}
              data-testid="meeting-card-import-action"
              style={{ flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                onImport(meeting);
              }}
            />
          </Tooltip>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--jerry-text-tertiary)",
          fontSize: "var(--jerry-font-size-sm)",
          marginBottom: "var(--jerry-sp-2)",
        }}
      >
        <ClockCircleOutlined style={{ flexShrink: 0 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {formatTimeRange(meeting.startsAt, meeting.endsAt)}
          {participantCount > 0 && ` · ${participantCount} 位参会人`}
          {meeting.organizerEmail && ` · 主持人 ${meeting.organizerEmail}`}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: "var(--jerry-sp-1)",
          flexWrap: "wrap",
          alignItems: "center",
          fontSize: "var(--jerry-font-size-xs)",
        }}
      >
        <MeetingProcessingStageTag stage={meeting.processingStage} />
        <Tag color={STATUS_TAG_COLOR[meeting.status]}>{MEETING_STATUS_LABELS[meeting.status]}</Tag>
        <Tag>{MEETING_SOURCE_LABELS[meeting.source]}</Tag>
        {imported ? (
          <>
            <Tag color={TRANSCRIPT_BADGE_COLOR[meeting.transcriptStatus]} style={{ marginInlineEnd: 0 }}>
              <AudioOutlined style={{ marginRight: 2 }} />
              {TRANSCRIPT_STATUS_LABELS[meeting.transcriptStatus]}
            </Tag>
            <AnalysisTag
              label="会后总结"
              state={meeting.summaryState}
              errorMessage={meeting.summaryError}
            />
            <AnalysisTag
              label="待办抽取"
              state={meeting.tasksState}
              errorMessage={meeting.tasksError}
            />
            <AnalysisTag
              label="会前准备"
              state={meeting.prepState}
              errorMessage={meeting.prepError}
            />
            {meeting.minutesDocumentId && (
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                <FileTextOutlined style={{ marginRight: 2 }} />
                纪要已归档
              </Tag>
            )}
          </>
        ) : (
          <Tag color="default" data-testid="meeting-card-not-imported">
            未导入内容
          </Tag>
        )}
      </div>
      {prepPrompted && (
        <div style={{ marginTop: "var(--jerry-sp-2)" }}>
          <Space size={4} wrap>
            <Tag color="gold" data-testid="meeting-card-prep-prompt">
              <BellOutlined style={{ marginRight: 4 }} />
              这场会可以先准备
            </Tag>
            {onPrepare && (
              <Button
                size="small"
                type="link"
                data-testid="meeting-card-prep-action"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrepare(meeting);
                }}
              >
                生成会前准备
              </Button>
            )}
          </Space>
        </div>
      )}
    </Card>
  );
}
